/**
 * Outil « Champ de directions » (Boîte à outils, Tle → prépa).
 *
 * Le champ de l'équation y′ = ay + b (ou y′ = f(x)) : en chaque point, un
 * petit segment de pente y′. Toucher le repère (ou « Ajouter un point ») pose
 * un point et trace la solution qui y passe, avec sa formule et le calcul de
 * la constante par la condition initiale ; trois points au plus, déplaçables
 * au doigt ou au clavier. Défi en cinq questions : reconnaître l'équation
 * d'un champ, puis trouver la solution qui vérifie une condition initiale
 * (en 0, en x₀ ≠ 0, ou pour y′ = f). Logique : `field.ts`.
 */

import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { cn } from '@jeraisonne/ui';
import {
  Note,
  PRIMARY_BUTTON,
  SECONDARY_BUTTON,
  Segmented,
  Stepper,
  ToggleChip,
} from '../../ui/controls';
import { makeFrame, snapPoint, type Frame, type Pt } from '../../ui/frame';
import { frenchSpaces } from '../../ui/format';
import { seededRng } from '../../ui/random';
import { DragHandle, FIGURE_BG_CLASS, HaloText, PlotGrid } from '../../ui/svg-parts';
import { useDrag } from '../../ui/use-drag';
import {
  A_VALUES,
  PRIMS,
  QUIZ_KINDS,
  WINDOW,
  bMax,
  behaviour,
  eqText,
  fieldNodes,
  generalSegs,
  makeQuiz,
  num,
  plainText,
  quizQuestion,
  slope,
  solutionThrough,
  type Eq,
  type PrimId,
  type Quiz,
  type Seg,
} from './field';

const SIZE = 320;
const SEG_PX = 13;
const NAMES = ['P', 'Q', 'R'] as const;
type Name = (typeof NAMES)[number];
const COLORS: Record<Name, string> = {
  P: 'var(--color-primary-500)',
  Q: 'var(--color-accent-600)',
  R: 'var(--color-success-600)',
};
/** trait des courbes : la bleue est éclaircie en sombre pour rester lisible */
const STROKE_CLASS: Record<Name, string> = {
  P: 'stroke-primary-500 ocean:stroke-primary-400',
  Q: 'stroke-accent-600',
  R: 'stroke-success-600',
};
const EQUILIBRIUM = 'var(--color-error-600)';
/** premiers nœuds proposés par « Ajouter un point » */
const FREE_SPOTS: readonly Pt[] = [
  { x: 0, y: 0 },
  { x: 0, y: 1 },
  { x: 0, y: -1 },
  { x: -1, y: 2 },
  { x: 1, y: -2 },
  { x: -2, y: 0.5 },
];

/** une écriture avec exposants : mise en forme à l'écran, lue « exposant » */
function Math_({ segs }: { segs: readonly Seg[] }) {
  return (
    <>
      <span aria-hidden>
        {segs.map((s, i) =>
          typeof s === 'string' ? (
            <Fragment key={i}>{frenchSpaces(s)}</Fragment>
          ) : (
            <sup key={i} className="text-[0.86em]">
              {s.sup}
            </sup>
          ),
        )}
      </span>
      <span className="sr-only">{frenchSpaces(plainText(segs, 'spoken'))}</span>
    </>
  );
}

/** les petits segments du champ */
function Field({ frame, eq }: { frame: Frame; eq: Eq }) {
  return (
    <g aria-hidden>
      {fieldNodes(frame.vp).map(({ x, y }) => {
        const s = slope(eq, x, y);
        // segment de longueur fixe à l'écran, de pente s (repère orthonormé)
        const len = Math.hypot(1, s);
        const dx = (SEG_PX / 2) * (1 / len);
        const dy = (SEG_PX / 2) * (s / len);
        const cx = frame.tx(x);
        const cy = frame.ty(y);
        return (
          <line
            key={`${x},${y}`}
            x1={cx - dx}
            y1={cy + dy}
            x2={cx + dx}
            y2={cy - dy}
            stroke="var(--color-ink-500)"
            strokeWidth={1.4}
            strokeLinecap="round"
          />
        );
      })}
    </g>
  );
}

/** graduations des axes, posées après le champ avec un liseré */
function AxisLabels({ frame }: { frame: Frame }) {
  const { tx, ty } = frame;
  const ticks = [-3, -2, -1, 1, 2, 3];
  return (
    <g aria-hidden>
      {ticks.map((v) => (
        <HaloText
          key={`x${v}`}
          x={tx(v)}
          y={ty(0) + 15}
          textAnchor="middle"
          fontSize={12}
          fill="var(--color-ink-600)"
        >
          {num(v)}
        </HaloText>
      ))}
      {ticks.map((v) => (
        <HaloText
          key={`y${v}`}
          x={tx(0) + 5}
          y={ty(v) + 4}
          fontSize={12}
          fill="var(--color-ink-600)"
        >
          {num(v)}
        </HaloText>
      ))}
    </g>
  );
}

/** tracé d'une solution sur la fenêtre (coupé hors cadre) */
function curvePath(frame: Frame, y: (x: number) => number): string {
  const { vp } = frame;
  const N = 320;
  let d = '';
  let pen = false;
  for (let i = 0; i <= N; i++) {
    const x = vp.xMin + ((vp.xMax - vp.xMin) * i) / N;
    const v = y(x);
    if (!Number.isFinite(v) || v < vp.yMin - 2 || v > vp.yMax + 2) {
      pen = false;
      continue;
    }
    d += `${pen ? 'L' : 'M'}${frame.tx(x).toFixed(1)},${frame.ty(v).toFixed(1)} `;
    pen = true;
  }
  return d;
}

function EquilibriumLine({ frame, m }: { frame: Frame; m: number }) {
  const { vp, tx, ty } = frame;
  if (m < vp.yMin || m > vp.yMax) return null;
  return (
    <g>
      <line
        x1={tx(vp.xMin)}
        x2={tx(vp.xMax)}
        y1={ty(m)}
        y2={ty(m)}
        stroke={EQUILIBRIUM}
        strokeWidth={1.8}
        strokeDasharray="6 4"
      />
      {/* à gauche : les courbes qui tendent vers m arrivent à droite */}
      <HaloText
        x={tx(vp.xMin) + 6}
        y={ty(m) + (m > vp.yMax - 0.6 ? 16 : -6)}
        fontSize={12}
        fontWeight={700}
        fill={EQUILIBRIUM}
      >
        {`y = ${num(m)}`}
      </HaloText>
    </g>
  );
}

/* ---------- explorer ---------- */

function Explore() {
  const [kind, setKind] = useState<'affine' | 'prim'>('affine');
  const [a, setA] = useState(-1);
  const [b, setB] = useState(2);
  const [pid, setPid] = useState<PrimId>('lin');
  const [pts, setPts] = useState<Partial<Record<Name, Pt>>>({ P: { x: 0, y: 0 } });
  const [order, setOrder] = useState<Name[]>(['P']);
  const [said, setSaid] = useState('');
  const svgRef = useRef<SVGSVGElement>(null);
  const addRef = useRef<HTMLButtonElement>(null);
  const adding = useRef<Name | null>(null);
  /** dernière position posée (le clavier annonce avant le rendu suivant) */
  const lastPos = useRef<Partial<Record<Name, Pt>>>({});
  const focusName = useRef<Name | 'add' | null>(null);
  const frame = makeFrame(WINDOW, SIZE);
  const eq: Eq = kind === 'affine' ? { kind, a, b } : { kind, id: pid };
  const ids = NAMES.filter((n) => pts[n]);
  const place = (p: Pt) => snapPoint(p, WINDOW, 0.5, 0.25);

  useEffect(() => {
    const f = focusName.current;
    focusName.current = null;
    if (f === 'add') addRef.current?.focus();
    else if (f) svgRef.current?.querySelector<SVGGElement>(`[aria-label^="Point ${f} "]`)?.focus();
  });

  const announce = (n: Name, p: Pt) =>
    setSaid(
      frenchSpaces(
        `${n}(${num(p.x)} ; ${num(p.y)}) : ${plainText(solutionThrough(eq, p).segs, 'spoken')}.`,
      ),
    );
  /** pose un point : un nom libre, sinon le plus ancien est remplacé */
  const add = (p: Pt): Name => {
    const free = NAMES.find((n) => !pts[n]);
    const name = free ?? order[0]!;
    const q = place(p);
    setPts((s) => ({ ...s, [name]: q }));
    setOrder((o) => [...o.filter((n) => n !== name), name]);
    announce(name, q);
    return name;
  };
  const addAtFreeSpot = () => {
    const taken = Object.values(pts);
    const spot =
      FREE_SPOTS.find((s) => !taken.some((t) => t && t.x === s.x && t.y === s.y)) ?? FREE_SPOTS[0]!;
    focusName.current = add(spot);
  };
  const drag = useDrag<Name>({
    svgRef,
    frame,
    ids,
    pointOf: (n) => pts[n]!,
    onDrag: (n, p) => {
      const q = place(p);
      lastPos.current[n] = q;
      setPts((s) => ({ ...s, [n]: q }));
    },
    onBackground: (p, phase) => {
      if (phase === 'start') adding.current = add(p);
      else if (adding.current) {
        const n = adding.current;
        const q = place(p);
        lastPos.current[n] = q;
        setPts((s) => ({ ...s, [n]: q }));
      }
    },
    onEnd: (n) => {
      adding.current = null;
      const p = lastPos.current[n] ?? pts[n];
      if (p) announce(n, p);
    },
    keyStep: 0.5,
  });

  const setAClamped = (v: number) => {
    setA(v);
    setB((x) => Math.max(-bMax(v), Math.min(bMax(v), x)));
  };
  const m = kind === 'affine' ? -b / a : null;
  const sols = ids.map((n) => ({ n, p: pts[n]!, s: solutionThrough(eq, pts[n]!) }));

  return (
    <div className="space-y-3">
      <Segmented
        ariaLabel="Équation"
        value={kind}
        options={[
          { value: 'affine', label: 'y′ = ay + b' },
          { value: 'prim', label: 'y′ = f(x)' },
        ]}
        onChange={setKind}
      />
      {kind === 'affine' ? (
        <div className="space-y-2">
          <div className="space-y-1">
            <p className="text-ink-500 text-xs font-semibold">a</p>
            <Segmented
              ariaLabel="Coefficient a"
              value={String(a)}
              options={A_VALUES.map((v) => ({ value: String(v), label: num(v) }))}
              onChange={(v) => setAClamped(Number(v))}
            />
          </div>
          <div className="w-44">
            <Stepper
              label="b"
              value={b}
              min={-bMax(a)}
              max={bMax(a)}
              step={0.5}
              onChange={setB}
              format={(v) => num(v)}
            />
          </div>
        </div>
      ) : (
        <div role="group" aria-label="Fonction f" className="flex flex-wrap gap-1.5">
          {PRIMS.map((p) => (
            <ToggleChip key={p.id} on={p.id === pid} onClick={() => setPid(p.id)}>
              <span aria-hidden>{`f(x) = ${p.fText}`}</span>
              <span className="sr-only">{`f(x) = ${p.spoken}`}</span>
            </ToggleChip>
          ))}
        </div>
      )}
      <p
        className="text-ink-900 text-base font-semibold"
        aria-live="polite"
        data-testid="df-equation"
      >
        {frenchSpaces(eqText(eq))}
        <span className="text-ink-600 font-normal">
          {frenchSpaces(' : solutions ')}
          <Math_ segs={generalSegs(eq)} />
        </span>
      </p>
      <div className="mx-auto w-full max-w-[26rem]">
        <svg
          ref={svgRef}
          viewBox={`0 0 ${SIZE} ${SIZE}`}
          className={cn(
            'border-ink-100 block aspect-square w-full touch-none select-none rounded-2xl border',
            FIGURE_BG_CLASS,
          )}
          role="group"
          aria-label={`Champ de directions de ${eqText(eq)}${m !== null ? `, segments horizontaux sur la droite y = ${num(m)}` : ''}. Touche le repère pour tracer une solution.`}
          data-testid="df-figure"
          {...drag.svgProps}
        >
          <PlotGrid frame={frame} step={1} labels={false} />
          <Field frame={frame} eq={eq} />
          <AxisLabels frame={frame} />
          {m !== null && <EquilibriumLine frame={frame} m={m} />}
          {sols.map(({ n, s }) => (
            <path
              key={`c${n}`}
              d={curvePath(frame, s.y)}
              fill="none"
              className={STROKE_CLASS[n]}
              strokeWidth={2.6}
            />
          ))}
          {sols.map(({ n, p }) => (
            <DragHandle
              key={n}
              x={frame.tx(p.x)}
              y={frame.ty(p.y)}
              color={COLORS[n]}
              label={n}
              name={`Point ${n} (${num(p.x)} ; ${num(p.y)})`}
              active={drag.active === n}
              keyProps={drag.keyProps(n)}
            />
          ))}
        </svg>
      </div>
      <ul className="space-y-2 text-sm" data-testid="df-solutions">
        {sols.map(({ n, p, s }) => (
          <li key={n} className="text-ink-900">
            <span
              aria-hidden
              className="mr-1.5 inline-block h-2.5 w-2.5 rounded-full align-middle"
              style={{ background: COLORS[n] }}
            />
            <span className="font-semibold">
              {frenchSpaces(`${n}(${num(p.x)} ; ${num(p.y)}) : `)}
            </span>
            <span className="whitespace-nowrap">
              <Math_ segs={s.segs} />
            </span>
            <span className="text-ink-600 block pl-4">
              <Math_ segs={s.how} />
            </span>
          </li>
        ))}
      </ul>
      <p className="sr-only" aria-live="polite" data-testid="df-live">
        {said}
      </p>
      <div className="flex flex-wrap gap-2">
        <button ref={addRef} type="button" className={SECONDARY_BUTTON} onClick={addAtFreeSpot}>
          Ajouter un point
        </button>
        <button
          type="button"
          className={cn(SECONDARY_BUTTON, 'disabled:opacity-40')}
          disabled={ids.length === 0}
          onClick={() => {
            setPts({});
            setOrder([]);
            setSaid('Solutions effacées.');
            focusName.current = 'add';
          }}
        >
          Effacer les solutions
        </button>
      </div>
      <Note live={false}>
        {kind === 'affine' ? (
          <>
            {frenchSpaces(
              `En chaque point (x ; y), le segment a pour pente y′ = ay + b : c’est la tangente de la solution qui passe par ce point. Les segments sont horizontaux sur la droite y = −b/a = ${num(m!)}, la solution constante. `,
            )}
            <Math_ segs={behaviour(a, b)} />
          </>
        ) : (
          frenchSpaces(
            'Ici la pente ne dépend que de x : les segments d’une même verticale sont parallèles. Les solutions sont les primitives de f, translatées verticalement les unes des autres.',
          )
        )}{' '}
        {frenchSpaces(
          'Par chaque point passe une et une seule solution : la condition initiale y(x₀) = y₀ fixe la constante C.',
        )}
      </Note>
      <p className="text-ink-500 text-xs">
        {frenchSpaces(
          'Touche le repère pour poser un point et tracer la solution qui y passe ; glisse-le pour le déplacer. Trois points au plus : au-delà, le plus ancien est remplacé. Au clavier : « Ajouter un point », puis Tab et les flèches (une demi-unité).',
        )}
      </p>
    </div>
  );
}

/* ---------- défi ---------- */

function Challenge({ seed }: { seed?: number }) {
  const rnd = useMemo(() => (seed === undefined ? Math.random : seededRng(seed)), [seed]);
  const rounds = QUIZ_KINDS.length;
  const [count, setCount] = useState(1);
  const [q, setQ] = useState<Quiz>(() => makeQuiz(QUIZ_KINDS[0]!, rnd));
  const [picked, setPicked] = useState<number | null>(null);
  const [score, setScore] = useState(0);
  const titleRef = useRef<HTMLParagraphElement>(null);
  const moved = useRef(false);
  const frame = makeFrame(WINDOW, SIZE);
  const m = q.eq.kind === 'affine' ? -q.eq.b / q.eq.a : null;
  const done = picked !== null && count >= rounds;
  const chosen = picked === null ? null : q.options[picked]!;
  const right = q.options.find((o) => o.correct)!;

  useEffect(() => {
    if (moved.current) titleRef.current?.focus();
    moved.current = false;
  }, [q]);

  const choose = (i: number) => {
    if (picked !== null) return;
    setPicked(i);
    if (q.options[i]!.correct) setScore(score + 1);
  };
  const next = () => {
    const c = done ? 1 : count + 1;
    if (done) setScore(0);
    setCount(c);
    moved.current = true;
    setQ(makeQuiz(QUIZ_KINDS[c - 1]!, rnd, q));
    setPicked(null);
  };
  const up = q.eq.kind === 'affine' && q.eq.a > 0;

  return (
    <div className="space-y-3">
      <p className="text-ink-500 text-xs font-semibold uppercase tracking-wide">
        {frenchSpaces(`Question ${count} / ${rounds} · ${score} juste${score > 1 ? 's' : ''}`)}
      </p>
      <p ref={titleRef} tabIndex={-1} className="text-ink-900 text-base font-semibold outline-none">
        {frenchSpaces(quizQuestion(q))}
      </p>
      <div className="mx-auto w-full max-w-[26rem]">
        <svg
          viewBox={`0 0 ${SIZE} ${SIZE}`}
          className={cn(
            'border-ink-100 block aspect-square w-full select-none rounded-2xl border',
            FIGURE_BG_CLASS,
          )}
          role="img"
          aria-label={
            q.kind === 'equation'
              ? `Champ de directions : segments horizontaux sur la droite y = ${num(m!)} ; au-dessus, ils ${up ? 'montent' : 'descendent'}.`
              : `Champ de directions de ${eqText(q.eq)} et le point A(${num(q.x0)} ; ${num(q.y0)}).`
          }
          data-testid="df-quiz-figure"
        >
          <PlotGrid frame={frame} step={1} labels={false} />
          <Field frame={frame} eq={q.eq} />
          <AxisLabels frame={frame} />
          {picked !== null && m !== null && <EquilibriumLine frame={frame} m={m} />}
          {picked !== null && chosen && !chosen.correct && chosen.y && (
            <path
              d={curvePath(frame, chosen.y)}
              fill="none"
              stroke="var(--color-error-600)"
              strokeWidth={2.4}
              strokeDasharray="6 4"
              data-testid="df-wrong-curve"
            />
          )}
          {picked !== null && right.y && (
            <path
              d={curvePath(frame, right.y)}
              fill="none"
              className={STROKE_CLASS.P}
              strokeWidth={2.6}
            />
          )}
          {q.kind !== 'equation' && (
            <g>
              <circle cx={frame.tx(q.x0)} cy={frame.ty(q.y0)} r={6} fill={COLORS.P} />
              <HaloText
                x={frame.tx(q.x0) - 16}
                y={frame.ty(q.y0) - 8}
                fontSize={14}
                fontWeight={700}
                fill="var(--color-ink-900)"
              >
                A
              </HaloText>
            </g>
          )}
        </svg>
      </div>
      <div role="group" aria-label="Réponses" className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        {q.options.map((o, i) => (
          <button
            key={plainText(o.segs)}
            type="button"
            onClick={() => choose(i)}
            aria-pressed={picked === i}
            aria-disabled={picked !== null && picked !== i}
            className={cn(
              'min-h-11 rounded-xl border px-3 py-2 text-center text-base font-semibold transition-colors',
              picked === null && 'border-ink-200 text-ink-800 hover:bg-ink-50',
              picked !== null && o.correct && 'border-success-600 bg-success-50 text-ink-900',
              picked === i && !o.correct && 'border-error-500 bg-error-50 text-ink-900',
              picked !== null && picked !== i && !o.correct && 'border-ink-100 text-ink-500',
            )}
          >
            <Math_ segs={o.segs} />
          </button>
        ))}
      </div>
      {chosen && (
        <Note live tone={chosen.correct ? 'success' : 'error'}>
          <span data-testid="df-feedback">
            {chosen.correct ? (
              <Math_ segs={q.explain} />
            ) : (
              <>
                <Math_ segs={chosen.hint ?? []} /> {frenchSpaces('La bonne réponse : ')}
                <span className="whitespace-nowrap">
                  <Math_ segs={right.segs} />.
                </span>
                {chosen.y && (
                  <span className="block pt-1 text-xs">
                    {frenchSpaces(
                      'En pointillés rouges, la courbe de ta réponse : elle ne suit pas les segments ou ne passe pas par A.',
                    )}
                  </span>
                )}
              </>
            )}
          </span>
        </Note>
      )}
      {picked !== null && (
        <div className="flex flex-wrap items-center gap-3">
          {done && (
            <p className="text-ink-900 text-sm font-semibold">
              {frenchSpaces(`Score : ${score} / ${rounds}`)}
            </p>
          )}
          <button type="button" className={done ? SECONDARY_BUTTON : PRIMARY_BUTTON} onClick={next}>
            {done ? 'Recommencer' : 'Question suivante'}
          </button>
        </div>
      )}
    </div>
  );
}

export function DirectionFieldTool({ seed }: { /** graine du défi (tests) */ seed?: number }) {
  const [mode, setMode] = useState<'explore' | 'quiz'>('explore');
  return (
    <div className="space-y-4">
      <Segmented
        ariaLabel="Ce qu’on fait"
        value={mode}
        options={[
          { value: 'explore', label: 'Explorer' },
          { value: 'quiz', label: 'Défi' },
        ]}
        onChange={setMode}
      />
      <div hidden={mode !== 'explore'}>
        <Explore />
      </div>
      <div hidden={mode !== 'quiz'}>
        <Challenge seed={seed} />
      </div>
    </div>
  );
}
